import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import api from '../../../services/api';

const useDashboardData = () => {
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandMetrics, setBrandMetrics] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs().subtract(1, 'day')
  ]);
  const [data, setData] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [channelData, setChannelData] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Fetch brands
  const fetchBrands = async () => {
    try {
      const response = await api.get('/brands');
      setBrands(response.data);
      if (response.data.length > 0) {
        setSelectedBrand(response.data[0].id);
      }
    } catch (error) {
      console.error('Markalar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch brand metrics
  const fetchBrandMetrics = async (brandId) => {
    try {
      const response = await api.get(`/brands/${brandId}/metrics`);
      setBrandMetrics(response.data);
    } catch (error) {
      console.error('Marka metrikleri yüklenirken hata:', error);
      setBrandMetrics([]);
    }
  };

  // Process data for charts
  const processDataForChart = useCallback((rawData) => {
    const groupedData = rawData.reduce((acc, item) => {
      const date = item.date;
      if (!acc[date]) {
        acc[date] = { date };
      }
      
      const metricInfo = brandMetrics.find(m => m.id === item.Metric.id);
      const metricLabel = metricInfo?.description || item.Metric.description || item.Metric.name;
      
      acc[date][metricLabel] = parseFloat(item.value);
      return acc;
    }, {});

    return Object.values(groupedData).sort((a, b) => 
      dayjs(a.date).valueOf() - dayjs(b.date).valueOf()
    );
  }, [brandMetrics]);

  // Generate summary data
  const generateSummaryData = useCallback((chartData) => {
    if (!chartData || chartData.length === 0) return;

    const latest = chartData[chartData.length - 1];
    const previous = chartData.length > 1 ? chartData[chartData.length - 2] : {};
    
    const metrics = Object.keys(latest).filter(key => key !== 'date');
    
    const topPriorityMetrics = ['REVENUE', 'ROI', 'COST(ALL)', 'COS'];
    
    const availableTopMetrics = metrics.filter(metric => topPriorityMetrics.includes(metric));
    const otherMetrics = metrics.filter(metric => !topPriorityMetrics.includes(metric));
    
    const sortedTopMetrics = topPriorityMetrics.filter(priority => availableTopMetrics.includes(priority));
    const sortedMetrics = [...sortedTopMetrics, ...otherMetrics];
    
    const summary = {};

    sortedMetrics.forEach(metric => {
      const totalValue = chartData.reduce((sum, day) => {
        const value = day[metric] || 0;
        return sum + value;
      }, 0);
      
      const avgValue = totalValue / chartData.length;
      const currentValue = latest[metric] || 0;
      const previousValue = previous[metric] || 0;
      const change = previousValue > 0 ? ((currentValue - previousValue) / previousValue) * 100 : 0;
      
      const summableMetrics = ['REVENUE', 'COST(ALL)', 'GADS COST (ALL)', 'META CR', 'BOOST ADS', 'TRANSACTION'];
      const displayValue = summableMetrics.some(summable => metric.includes(summable.replace(/[()]/g, ''))) 
        ? totalValue 
        : avgValue;
      
      summary[metric] = {
        current: displayValue,
        total: totalValue,
        average: avgValue,
        lastDay: currentValue,
        previous: previousValue,
        change: change,
        trend: change > 0 ? 'up' : change < 0 ? 'down' : 'stable'
      };
    });

    setSummaryData(summary);
    generateChannelData(chartData);
  }, []);

  // Generate channel data
  const generateChannelData = (chartData) => {
    if (!chartData || chartData.length === 0) return;

    const totals = chartData.reduce((acc, day) => {
      Object.keys(day).forEach(key => {
        if (key !== 'date') {
          acc[key] = (acc[key] || 0) + (parseFloat(day[key]) || 0);
        }
      });
      return acc;
    }, {});
    
    const channels = [
      { name: 'Google Reklamları', key: 'GADS COST (ALL)', color: '#FF4444' },
      { name: 'Meta CR', key: 'META CR', color: '#1890FF' },
      { name: 'Boost Reklamları', key: 'BOOST ADS', color: '#722ED1' },
      { name: 'Diğer Kanallar', key: 'OTHER', color: '#8C8C8C' }
    ];

    const channelValues = channels.map(channel => {
      if (channel.key === 'OTHER') {
        const totalCost = totals['COST(ALL)'] || 0;
        const metaCrCost = totals['META CR'] || 0;
        const googleCost = totals['GADS COST (ALL)'] || 0;
        const boostCost = totals['BOOST ADS'] || 0;
        const otherCost = Math.max(0, totalCost - metaCrCost - googleCost - boostCost);
        
        return {
          ...channel,
          value: otherCost,
          tooltip: `Diğer Kanallar: ${new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(otherCost)} (${chartData.length} gün toplamı)`
        };
      }
      
      const value = totals[channel.key] || 0;
      return {
        ...channel,
        value: value,
        tooltip: `${channel.name}: ${new Intl.NumberFormat('tr-TR', {
          style: 'currency',
          currency: 'TRY',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value)} (${chartData.length} gün toplamı)`
      };
    }).filter(channel => channel.value > 0);

    setChannelData(channelValues);
  };

  // Fetch comparison data for comparison mode
  const fetchComparisonData = useCallback(async (comparisonPeriod, customDateRange = null) => {
    if (!selectedBrand || !dateRange || brandMetrics.length === 0) return null;
    
    try {
      setComparisonLoading(true);
      
      let previousStartDate, previousEndDate;
      
      if (comparisonPeriod === 'custom' && customDateRange) {
        // Use custom date range
        previousStartDate = customDateRange[0];
        previousEndDate = customDateRange[1];
      } else {
        // Calculate previous period dates based on current date range
        const currentStartDate = dateRange[0];
        const currentEndDate = dateRange[1];
        const daysDiff = currentEndDate.diff(currentStartDate, 'days');

        switch (comparisonPeriod) {
          case 'previous_week':
            previousStartDate = currentStartDate.subtract(7, 'days');
            previousEndDate = currentEndDate.subtract(7, 'days');
            break;
          case 'previous_month':
            previousStartDate = currentStartDate.subtract(1, 'month');
            previousEndDate = currentEndDate.subtract(1, 'month');
            break;
          case 'previous_quarter':
            previousStartDate = currentStartDate.subtract(3, 'months');
            previousEndDate = currentEndDate.subtract(3, 'months');
            break;
          case 'previous_year':
            previousStartDate = currentStartDate.subtract(1, 'year');
            previousEndDate = currentEndDate.subtract(1, 'year');
            break;
          case 'same_period_last_year':
            previousStartDate = currentStartDate.subtract(1, 'year');
            previousEndDate = currentEndDate.subtract(1, 'year');
            break;
          default:
            previousStartDate = currentStartDate.subtract(daysDiff, 'days');
            previousEndDate = currentStartDate.subtract(1, 'day');
        }
      }
      
      const comparisonResponse = await api.get('/data', {
        params: {
          brandId: selectedBrand,
          startDate: previousStartDate.format('YYYY-MM-DD'),
          endDate: previousEndDate.format('YYYY-MM-DD')
        }
      });
      
      const processedComparisonData = processDataForChart(comparisonResponse.data);
      const comparisonSummary = generateComparisonSummary(processedComparisonData);
      
      const result = {
        data: processedComparisonData,
        summary: comparisonSummary,
        period: comparisonPeriod,
        isCustom: comparisonPeriod === 'custom',
        startDate: previousStartDate.format('YYYY-MM-DD'),
        endDate: previousEndDate.format('YYYY-MM-DD')
      };
      
      setComparisonData(result);
      return result;
    } catch (error) {
      console.error('Karşılaştırma verileri yüklenirken hata:', error);
      setComparisonData(null);
      return null;
    } finally {
      setComparisonLoading(false);
    }
  }, [selectedBrand, dateRange, brandMetrics, processDataForChart]);

  // Generate comparison summary data
  const generateComparisonSummary = useCallback((chartData) => {
    if (!chartData || chartData.length === 0) return {};

    const latest = chartData[chartData.length - 1];
    const metrics = Object.keys(latest).filter(key => key !== 'date');
    const summary = {};

    metrics.forEach(metric => {
      const totalValue = chartData.reduce((sum, day) => {
        const value = day[metric] || 0;
        return sum + value;
      }, 0);
      
      const avgValue = totalValue / chartData.length;
      const summableMetrics = ['REVENUE', 'COST(ALL)', 'GADS COST (ALL)', 'META CR', 'BOOST ADS', 'TRANSACTION'];
      const displayValue = summableMetrics.some(summable => metric.includes(summable.replace(/[()]/g, ''))) 
        ? totalValue 
        : avgValue;
      
      summary[metric] = {
        current: displayValue,
        total: totalValue,
        average: avgValue,
        trend: 'stable'
      };
    });

    return summary;
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!selectedBrand || !dateRange || brandMetrics.length === 0) return;
    
    try {
      setLoading(true);
      
      const dailyResponse = await api.get('/data', {
        params: {
          brandId: selectedBrand,
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD')
        }
      });
      
      const processedData = processDataForChart(dailyResponse.data);
      setData(processedData);
      generateSummaryData(processedData);
    } catch (error) {
      console.error('Veriler yüklenirken hata:', error);
      setData([]);
      setSummaryData({});
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, dateRange, brandMetrics, processDataForChart, generateSummaryData]);

  // Initialize
  useEffect(() => {
    fetchBrands();
  }, []);

  // Fetch metrics when brand changes
  useEffect(() => {
    if (selectedBrand) {
      fetchBrandMetrics(selectedBrand);
    }
  }, [selectedBrand]);

  // Fetch data when dependencies change
  useEffect(() => {
    if (selectedBrand && dateRange && brandMetrics.length > 0) {
      fetchData();
    }
  }, [selectedBrand, dateRange, brandMetrics, fetchData]);

  return {
    loading,
    brands,
    selectedBrand,
    setSelectedBrand,
    dateRange,
    setDateRange,
    data,
    summaryData,
    channelData,
    brandMetrics,
    comparisonData,
    comparisonLoading,
    fetchComparisonData,
    refetch: fetchData
  };
};

export default useDashboardData;